import React, { useState, useEffect } from 'react';
import api from '../services/api';

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const WEEKS = [
  { value: 1, label: '1st week' },
  { value: 2, label: '2nd week' },
  { value: 3, label: '3rd week' },
  { value: 4, label: '4th week' },
  { value: -1, label: 'Last week' },
];

const needsDayPick   = (f) => ['weekly','biweekly','monthly','2x_week','3x_week'].includes(f);
const needsMultiDay  = (f) => ['2x_week','3x_week'].includes(f);
const needsWeekPick  = (f) => f === 'biweekly' || f === 'monthly';
const maxDays        = (f) => f === '2x_week' ? 2 : f === '3x_week' ? 3 : 1;

const freqLabel = (f) => ({
  daily:'Daily (Mon-Fri)', '3x_week':'3x per Week', '2x_week':'2x per Week',
  weekly:'Weekly', biweekly:'Bi-Weekly', monthly:'Monthly',
  bimonthly:'Bi-Monthly', quarterly:'Quarterly', annual:'Annual', one_time:'One-Time'
}[f] || f);


const ConflictModal = ({ conflict, techName, onClose }) => {
  if (!conflict) return null;
  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.6)',zIndex:999999,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:'white',borderRadius:'12px',width:'95%',maxWidth:'520px',overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
        <div style={{background:'linear-gradient(135deg,#B91C1C,#7F1D1D)',padding:'18px 24px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <div style={{color:'white',fontSize:'16px',fontWeight:'700'}}>⚠ Scheduling Conflict</div>
            <div style={{color:'rgba(255,255,255,0.7)',fontSize:'12px',marginTop:'3px'}}>{techName} is already scheduled on these dates</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',color:'white',fontSize:'22px',cursor:'pointer'}}>&times;</button>
        </div>
        <div style={{padding:'20px 24px',maxHeight:'60vh',overflowY:'auto'}}>
          <div style={{fontSize:'13px',color:'#374151',marginBottom:'16px'}}>
            The following dates have conflicts with existing work orders. Please select a <strong>different technician</strong> or adjust the <strong>start time</strong>.
          </div>
          {conflict.conflict_dates.map((cd, idx) => (
            <div key={idx} style={{border:'1px solid #FECACA',borderRadius:'8px',padding:'12px 14px',marginBottom:'10px',background:'#FEF2F2'}}>
              <div style={{fontWeight:'700',color:'#991B1B',fontSize:'14px',marginBottom:'8px'}}>
                📅 {new Date(cd.date + 'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})}
              </div>
              {cd.conflicts.map((c, j) => (
                <div key={j} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 10px',background:'white',borderRadius:'6px',marginBottom:'4px',border:'1px solid #FCA5A5'}}>
                  <div style={{fontSize:'20px'}}>📋</div>
                  <div>
                    <div style={{fontSize:'13px',fontWeight:'600',color:'#1F2937'}}>{c.order_number} — {c.facility_name}</div>
                    <div style={{fontSize:'12px',color:'#6B7280'}}>
                      🕐 {c.scheduled_time_start?.substring(0,5)} – {c.scheduled_time_end?.substring(0,5)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{padding:'16px 24px',borderTop:'1px solid #E5E7EB',background:'#F9FAFB',display:'flex',justifyContent:'flex-end'}}>
          <button onClick={onClose} style={{padding:'10px 24px',border:'none',borderRadius:'7px',background:'#2D5C3F',color:'white',cursor:'pointer',fontWeight:'700',fontSize:'13px'}}>
            ✓ OK, I'll Adjust
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ActivateServicesModal({ proposal, onSuccess, onSkip }) {
  const today = new Date();
  const localToday = today.getFullYear() + '-' +
    String(today.getMonth()+1).padStart(2,'0') + '-' +
    String(today.getDate()).padStart(2,'0');

  const [technicians, setTechnicians] = useState([]);
  const [form, setForm] = useState({
    start_date: localToday,
    technician_id: '',
    preferred_time_start: '08:00',
    estimated_duration: 120,
    day_of_week: 1,
    days_of_week: [],
    weeks: [1,3],
    week_of_month: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [conflict, setConflict] = useState(null); // {message, conflicts[]}
  const [checking, setChecking] = useState(false);

  const freq = proposal?.frequency || 'monthly';
  const isOneTime = freq === 'one_time';

  useEffect(() => {
    api.get('/technicians').then(r => {
      setTechnicians(r.data?.data || r.data || []);
    }).catch(() => {});
  }, []);

  const toggleDay = (d) => {
    const max = maxDays(freq);
    if (needsMultiDay(freq)) {
      setForm(f => {
        const cur = f.days_of_week;
        if (cur.includes(d)) return { ...f, days_of_week: cur.filter(x => x !== d) };
        if (cur.length >= max) return f;
        return { ...f, days_of_week: [...cur, d] };
      });
    } else {
      setForm(f => ({ ...f, day_of_week: d }));
    }
  };

  const toggleBiweeklyWeek = (w) => {
    setForm(f => {
      const cur = f.weeks;
      if (cur.includes(w)) return cur.length > 1 ? { ...f, weeks: cur.filter(x => x !== w) } : f;
      return { ...f, weeks: [...cur, w].sort() };
    });
  };

  const buildConfig = () => {
    if (freq === 'weekly')   return { day_of_week: form.day_of_week };
    if (freq === '2x_week')  return { days_of_week: form.days_of_week };
    if (freq === '3x_week')  return { days_of_week: form.days_of_week };
    if (freq === 'daily')    return {};
    if (freq === 'biweekly') return { weeks: form.weeks, day_of_week: form.day_of_week };
    if (freq === 'monthly')  return { week_of_month: form.week_of_month, day_of_week: form.day_of_week };
    return {};
  };

  const validate = () => {
    if (!form.start_date) return 'Service start date is required.';
    if (!form.technician_id) return 'Please assign a technician before generating work orders.';
    if (needsMultiDay(freq) && form.days_of_week.length !== maxDays(freq))
      return `Please select exactly ${maxDays(freq)} days for ${freqLabel(freq)}.`;
    return null;
  };

  const techName = technicians.find(t => String(t.id) === String(form.technician_id));
  const techFullName = techName ? techName.first_name + ' ' + techName.last_name : 'Technician';

  const checkAvailability = async () => {
    if (!form.technician_id || !form.start_date || !form.preferred_time_start) return true;
    setChecking(true);
    setConflict(null);
    try {
      const startTime = form.preferred_time_start + ':00';
      const [h, m] = form.preferred_time_start.split(':').map(Number);
      const totalMin = h * 60 + m + parseInt(form.estimated_duration || 120);
      const endTime = String(Math.floor(totalMin/60)).padStart(2,'0') + ':' + String(totalMin%60).padStart(2,'00') + ':00';
      const r = await api.post('/service-orders/check-availability', {
        technician_id: form.technician_id,
        scheduled_date: form.start_date,
        scheduled_time_start: startTime,
        scheduled_time_end: endTime,
      });
      if (!r.data.available) {
        setConflict(r.data);
        setChecking(false);
        return false;
      }
    } catch (e) { /* if endpoint fails, allow */ }
    setChecking(false);
    return true;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    const available = await checkAvailability();
    if (!available) return;
    setLoading(true); setError('');
    try {
      const payload = {
        start_date:            form.start_date,
        technician_id:         form.technician_id || null,
        preferred_time_start:  form.preferred_time_start + ':00',
        estimated_duration:    parseInt(form.estimated_duration),
        recurrence_config:     buildConfig(),
      };
      const res = await api.post('/recurring/from-proposal/' + proposal.id, payload);
      onSuccess(res.data);
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.conflict_dates) {
        setConflict(e.response.data);
      } else {
        setError(e.response?.data?.message || 'Error activating services');
      }
    }
    setLoading(false);
  };

  const S = {
    overlay:  { position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.55)',zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center' },
    box:      { background:'white',borderRadius:'14px',width:'95%',maxWidth:'520px',overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.3)' },
    header:   { background:'linear-gradient(135deg,#2D5C3F,#1A3A2A)',padding:'20px 24px',display:'flex',justifyContent:'space-between',alignItems:'center' },
    body:     { padding:'24px',maxHeight:'65vh',overflowY:'auto' },
    footer:   { padding:'16px 24px',borderTop:'1px solid #E5E7EB',display:'flex',gap:'10px',justifyContent:'flex-end',background:'#F9FAFB' },
    label:    { display:'block',fontSize:'12px',fontWeight:'700',color:'#374151',marginBottom:'5px',textTransform:'uppercase',letterSpacing:'0.4px' },
    input:    { width:'100%',padding:'9px 12px',border:'1px solid #D1D5DB',borderRadius:'7px',fontSize:'14px',boxSizing:'border-box' },
    section:  { marginBottom:'18px' },
    dayBtn:   (active) => ({ padding:'7px 11px',borderRadius:'6px',border:'2px solid ' + (active ? '#2D5C3F' : '#D1D5DB'),background: active ? '#2D5C3F' : 'white',color: active ? 'white' : '#374151',cursor:'pointer',fontSize:'13px',fontWeight:'600' }),
    badge:    { display:'inline-block',padding:'3px 10px',borderRadius:'20px',background:'#D1FAE5',color:'#065F46',fontSize:'12px',fontWeight:'700',marginLeft:'8px' },
    errBox:   { background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:'7px',padding:'10px 14px',color:'#DC2626',fontSize:'13px',marginBottom:'14px' },
    skipBtn:  { padding:'9px 18px',border:'1px solid #D1D5DB',borderRadius:'7px',background:'white',color:'#6B7280',cursor:'pointer',fontSize:'13px',fontWeight:'600' },
    saveBtn:  { padding:'9px 22px',border:'none',borderRadius:'7px',background: (loading || checking || conflict) ? '#9CA3AF' : '#2D5C3F',color:'white',cursor: (loading || checking || conflict) ? 'not-allowed' : 'pointer',fontSize:'13px',fontWeight:'700' },
  };

  return (
    <div style={S.overlay}>
      <div style={S.box}>
        <div style={S.header}>
          <div>
            <div style={{color:'white',fontSize:'16px',fontWeight:'700'}}>
              &#128197; Activate Services
              <span style={S.badge}>{freqLabel(freq)}</span>
            </div>
            <div style={{color:'rgba(255,255,255,0.65)',fontSize:'12px',marginTop:'3px'}}>
              {proposal?.client_name} &mdash; {proposal?.service_type}
              {proposal?.fifth_week_vip && <span style={{color:'#D4AF37',marginLeft:'8px'}}>&#11088; VIP</span>}
            </div>
          </div>
          <button onClick={onSkip} style={{background:'none',border:'none',color:'rgba(255,255,255,0.7)',fontSize:'22px',cursor:'pointer'}}>&times;</button>
        </div>

        <div style={S.body}>
          {isOneTime ? (
            <div style={{textAlign:'center',padding:'30px 0',color:'#6B7280'}}>
              <div style={{fontSize:'40px',marginBottom:'12px'}}>&#128203;</div>
              <div style={{fontSize:'15px',fontWeight:'600',color:'#374151'}}>One-Time Service</div>
              <div style={{fontSize:'13px',marginTop:'6px'}}>No recurring schedule needed for one-time services.</div>
            </div>
          ) : (
            <>
              {error && <div style={S.errBox}>{error}</div>}


              <div style={S.section}>
                <label style={S.label}>Service Start Date *</label>
                <input style={S.input} type="date" value={form.start_date}
                  onChange={e => { setConflict(null); setForm(f => ({...f, start_date: e.target.value})); }} />
              </div>

              <div style={S.section}>
                <label style={S.label}>Assign Technician</label>
                <select style={S.input} value={form.technician_id}
                  onChange={e => { setConflict(null); setForm(f => ({...f, technician_id: e.target.value})); }}>
                  <option value="">-- Unassigned --</option>
                  {technicians.map(t => (
                    <option key={t.id} value={t.id}>{t.first_name} {t.last_name} ({t.role})</option>
                  ))}
                </select>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px',marginBottom:'18px'}}>
                <div>
                  <label style={S.label}>Preferred Start Time *</label>
                  <input style={S.input} type="time" value={form.preferred_time_start}
                    onChange={e => { setConflict(null); setForm(f => ({...f, preferred_time_start: e.target.value})); }} />
                </div>
                <div>
                  <label style={S.label}>Duration (minutes) *</label>
                  <input style={S.input} type="number" min="30" step="15" value={form.estimated_duration}
                    onChange={e => setForm(f => ({...f, estimated_duration: e.target.value}))} />
                </div>
              </div>

              {needsDayPick(freq) && (
                <div style={S.section}>
                  <label style={S.label}>
                    {needsMultiDay(freq)
                      ? `Service Days (select ${maxDays(freq)})`
                      : 'Service Day'}
                  </label>
                  <div style={{display:'flex',gap:'7px',flexWrap:'wrap',marginTop:'6px'}}>
                    {DAYS.map((d,i) => {
                      const active = needsMultiDay(freq)
                        ? form.days_of_week.includes(i)
                        : form.day_of_week === i;
                      return (
                        <button key={i} style={S.dayBtn(active)} onClick={() => toggleDay(i)}>{d}</button>
                      );
                    })}
                  </div>
                </div>
              )}

              {freq === 'biweekly' && (
                <div style={S.section}>
                  <label style={S.label}>Service Weeks (select 2)</label>
                  <div style={{display:'flex',gap:'7px',flexWrap:'wrap',marginTop:'6px'}}>
                    {WEEKS.map(w => (
                      <button key={w.value} style={S.dayBtn(form.weeks.includes(w.value))}
                        onClick={() => toggleBiweeklyWeek(w.value)}>{w.label}</button>
                    ))}
                  </div>
                </div>
              )}

              {freq === 'monthly' && (
                <div style={S.section}>
                  <label style={S.label}>Week of Month</label>
                  <div style={{display:'flex',gap:'7px',flexWrap:'wrap',marginTop:'6px'}}>
                    {WEEKS.map(w => (
                      <button key={w.value} style={S.dayBtn(form.week_of_month === w.value)}
                        onClick={() => setForm(f => ({...f, week_of_month: w.value}))}>{w.label}</button>
                    ))}
                  </div>
                </div>
              )}

              {proposal?.fifth_week_vip && (
                <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:'8px',padding:'10px 14px',fontSize:'13px',color:'#92400E'}}>
                  &#11088; <strong>VIP Client:</strong> 5th week visits will be scheduled as complimentary services (non-billable).
                </div>
              )}
            </>
          )}
        </div>

        <div style={S.footer}>
          <button style={S.skipBtn} onClick={onSkip}>
            {isOneTime ? 'Continue' : 'Skip for now'}
          </button>
          {!isOneTime && (
            <button style={S.saveBtn} onClick={handleSubmit} disabled={loading || checking || !!conflict}>
              {checking ? 'Checking availability...' : loading ? 'Activating...' : '▶ Activate & Generate WOs'}
            </button>
          )}
        </div>
      </div>
      {conflict && <ConflictModal conflict={conflict} techName={techFullName} onClose={() => setConflict(null)} />}
    </div>
  );
}
